'use client';

import { startTransition, useOptimistic, useState } from 'react';

import { usePatchJobDeliverableMutation } from '@/features/jobs/api/jobsApi';
import { Badge, Popover } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';

type DeliverableStatus = 'backlog' | 'in_progress' | 'completed' | string;

type DeliverableStatusControlProps = {
  jobId?: string;
  deliverableId: string;
  value: DeliverableStatus;
  readOnly: boolean;
  onPatched?: (patch: Partial<JobDeliverable>) => void;
};

const STATUS_OPTIONS = [
  { value: 'backlog', label: 'Backlog', tone: 'default' },
  { value: 'in_progress', label: 'In Progress', tone: 'info' },
  { value: 'completed', label: 'Completed', tone: 'success' },
] as const;

function getStatusOption(status: DeliverableStatus) {
  return (
    STATUS_OPTIONS.find((option) => option.value === status) ??
    STATUS_OPTIONS[0]
  );
}

export function DeliverableStatusControl({
  jobId,
  deliverableId,
  value,
  readOnly,
  onPatched,
}: DeliverableStatusControlProps) {
  const [patchDeliverable, { isLoading }] = usePatchJobDeliverableMutation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [committedStatus, setCommittedStatus] =
    useState<DeliverableStatus>(value);
  const [optimisticStatus, setOptimisticStatus] =
    useOptimistic(committedStatus);

  const statusOption = getStatusOption(optimisticStatus);
  const isBusy = readOnly || isLoading;

  const handleStatusChange = async (nextStatus: DeliverableStatus) => {
    const previousStatus = optimisticStatus;

    if (nextStatus === previousStatus) {
      setAnchorEl(null);
      return;
    }

    startTransition(() => {
      setOptimisticStatus(nextStatus);
    });
    setCommittedStatus(nextStatus);
    setAnchorEl(null);

    try {
      const response = await patchDeliverable({
        ...(jobId ? { jobId } : {}),
        deliverableId,
        payload: { status: nextStatus },
      }).unwrap();
      onPatched?.(response.data ?? { status: nextStatus });
    } catch (error) {
      startTransition(() => {
        setOptimisticStatus(previousStatus);
      });
      setCommittedStatus(previousStatus);
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.metricCard}>
      <button
        type="button"
        aria-label={`Update deliverable status: ${statusOption.label}`}
        className={styles.inlineMetaButton}
        disabled={isBusy}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Badge label={statusOption.label} tone={statusOption.tone} size="lg" />
      </button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <div className={styles.inlineMetaMenu} role="menu">
          {STATUS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitem"
              className="action-item"
              disabled={isBusy}
              onClick={() => {
                void handleStatusChange(option.value);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Popover>
    </span>
  );
}
